"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Info, X, XCircle } from "lucide-react";
import { profileImageUrl } from "@/lib/profile-image";
import { useScrollLock, useSheetDrag } from "./sheet";
import { announceDialogOpened } from "./topLayer";
import "./ui.css";

/**
 * THE v2 BASE COMPONENTS.
 *
 * Dialog, popover, toast, alert, field, switch, segmented control, tooltip and
 * spinner — everything a screen needs before it has content of its own, in the
 * same vocabulary as the page.
 *
 * TWO DELIBERATE CHOICES WORTH DEFENDING:
 *
 * 1. The dialog is a native `<dialog>` opened with `showModal()`. That hands
 *    Escape, the top layer, the backdrop and inertness of the rest of the page
 *    to the platform. A hand-rolled overlay means hand-rolling a focus trap,
 *    and a focus trap is always wrong in some case nobody thought of.
 *
 * 2. The toast store is eleven lines of `useSyncExternalStore` rather than a
 *    context provider. Toasts are fired from anywhere — including from code
 *    that is not under any provider — and the only state is an array. A
 *    provider would add a tree dependency for nothing.
 */

/* ===================================================================== */
/* The eight-point star, at spinner size. The page's motif, turning.      */
/* ===================================================================== */
export function Spinner(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  const t1 = `v2-spin ${className ?? ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <g stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"><path d="M12 1.5 22.5 12 12 22.5 1.5 12Z" opacity=".35" /><path d="M12 1.5 22.5 12 12 22.5" /></g>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <svg className={t1} viewBox="0 0 24 24" fill="none" aria-hidden={true}>{t2}</svg>;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}

/* ===================================================================== */
/* Toasts                                                                 */
/* ===================================================================== */

export type Tone = "info" | "success" | "warn" | "danger" | "gold";
export type ToastInput = {
  title: string;
  body?: string;
  tone?: Tone;
  ms?: number;
};
type ToastItem = ToastInput & {
  id: number;
  ms: number;
  tone: Tone;
};
let items: ToastItem[] = [];
let seq = 0;
const subs = new Set<() => void>();
const emit = () => subs.forEach(f => f());

/** Fire a toast from anywhere — no provider, no hook, no tree dependency. */
export function toast(input: ToastInput) {
  const item: ToastItem = {
    tone: "info",
    ms: 4200,
    ...input,
    id: ++seq
  };
  items = [...items, item].slice(-4);
  emit();
  window.setTimeout(() => dismissToast(item.id), item.ms);
  return item.id;
}
export function dismissToast(id: number) {
  const next = items.filter(t => t.id !== id);
  if (next.length === items.length) return;
  items = next;
  emit();
}
const subscribe = (cb: () => void) => {
  subs.add(cb);
  return () => void subs.delete(cb);
};
const getSnapshot = () => items;
// The server renders nothing; a stable empty array keeps this from looping.
const EMPTY: ToastItem[] = [];
const getServerSnapshot = () => EMPTY;
const TONE_ICON: Record<Tone, typeof Info> = {
  info: Info,
  success: Check,
  warn: AlertTriangle,
  danger: XCircle,
  gold: Info
};
export function Toaster(t0) {
  const $ = _c(7);
  const {
    dismissLabel
  } = t0;
  const list = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (list.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== dismissLabel || $[1] !== list) {
    let t2;
    if ($[3] !== dismissLabel) {
      t2 = t => {
        const Icon = TONE_ICON[t.tone];
        return <div className="v2-toast" data-tone={t.tone} key={t.id}><Icon aria-hidden={true} /><span><b>{t.title}</b>{t.body ? <span>{t.body}</span> : null}</span><button type="button" className="v2-x" onClick={() => dismissToast(t.id)} aria-label={dismissLabel}><X aria-hidden={true} /></button><span className="v2-toast-life" style={{
            animationDuration: `${t.ms}ms`
          }} aria-hidden={true} /></div>;
      };
      $[3] = dismissLabel;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = list.map(t2);
    $[0] = dismissLabel;
    $[1] = list;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="v2-toaster" role="region" aria-live="polite">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/* ===================================================================== */
/* Dialog                                                                 */
/* ===================================================================== */
export function Dialog(t0) {
  const $ = _c(52);
  const {
    open,
    onClose,
    title,
    closeLabel,
    children,
    footer,
    className,
    hideClose: t1,
    modal: t2,
    busy: t3
  } = t0;
  const hideClose = t1 === undefined ? false : t1;
  const modal = t2 === undefined ? true : t2;
  const busy = t3 === undefined ? false : t3;
  const ref = useRef(null);
  const panelRef = useRef(null);
  const busyRef = useRef(busy);
  let t4;
  let t5;
  if ($[0] !== busy) {
    t4 = () => {
      busyRef.current = busy;
    };
    t5 = [busy];
    $[0] = busy;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  let t6;
  if ($[3] !== onClose) {
    t6 = () => {
      if (!busyRef.current) {
        onClose();
      }
    };
    $[3] = onClose;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const requestClose = t6;
  useScrollLock(open);
  useSheetDrag(panelRef, open, requestClose);
  let t7;
  let t8;
  if ($[5] !== modal || $[6] !== open) {
    t7 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      if (open && !el.open) {
        if (modal) {
          el.showModal();
        } else {
          el.show();
        }
        announceDialogOpened();
      }
      if (!open && el.open) {
        el.close();
      }
    };
    t8 = [open, modal];
    $[5] = modal;
    $[6] = open;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[9] !== modal || $[10] !== open || $[11] !== requestClose) {
    t9 = () => {
      const el_0 = ref.current;
      if (modal || !open || !el_0) {
        return;
      }
      const onKey = e => {
        if (e.key === "Escape") {
          e.preventDefault();
          requestClose();
          return;
        }
        if (e.key !== "Tab") {
          return;
        }
        const items = [...el_0.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex=\"-1\"])")];
        if (!items.length) {
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const at = document.activeElement;
        if (!el_0.contains(at)) {
          e.preventDefault();
          first.focus();
        } else {
          if (e.shiftKey && at === first) {
            e.preventDefault();
            last.focus();
          } else {
            if (!e.shiftKey && at === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    };
    t10 = [modal, open, requestClose];
    $[9] = modal;
    $[10] = open;
    $[11] = requestClose;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[14] !== requestClose) {
    t11 = () => {
      const el_1 = ref.current;
      if (!el_1) {
        return;
      }
      const onCancel = e_0 => {
        e_0.preventDefault();
        requestClose();
      };
      el_1.addEventListener("cancel", onCancel);
      return () => el_1.removeEventListener("cancel", onCancel);
    };
    t12 = [requestClose];
    $[14] = requestClose;
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  useEffect(t11, t12);
  let t13;
  if ($[17] !== modal || $[18] !== open || $[19] !== requestClose) {
    t13 = !modal && open ? <div className="v2-dialog-scrim" aria-hidden={true} onClick={requestClose} /> : null;
    $[17] = modal;
    $[18] = open;
    $[19] = requestClose;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = className ? `v2-dialog ${className}` : "v2-dialog";
  const t15 = busy || undefined;
  const t16 = modal ? undefined : true;
  const t17 = modal ? undefined : "false";
  let t18;
  if ($[21] !== requestClose) {
    t18 = e_1 => {
      if (e_1.target === ref.current) {
        requestClose();
      }
    };
    $[21] = requestClose;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== title) {
    t19 = <h2>{title}</h2>;
    $[23] = title;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== busy || $[26] !== closeLabel || $[27] !== hideClose || $[28] !== requestClose) {
    t20 = hideClose ? null : <button type="button" className="v2-x" onClick={requestClose} disabled={busy} aria-label={closeLabel}><X aria-hidden={true} /></button>;
    $[25] = busy;
    $[26] = closeLabel;
    $[27] = hideClose;
    $[28] = requestClose;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <div className="v2-panel-head">{t19}{t20}</div>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== children) {
    t22 = <div className="v2-panel-body">{children}</div>;
    $[33] = children;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== footer) {
    t23 = footer ? <div className="v2-panel-foot">{footer}</div> : null;
    $[35] = footer;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t21 || $[38] !== t22 || $[39] !== t23) {
    t24 = <div className="v2-panel" ref={panelRef}>{t21}{t22}{t23}</div>;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t14 || $[42] !== t15 || $[43] !== t16 || $[44] !== t17 || $[45] !== t18 || $[46] !== t24 || $[47] !== title) {
    t25 = <dialog className={t14} ref={ref} aria-label={title} aria-busy={t15} aria-modal={t16} data-modal={t17} onClick={t18}>{t24}</dialog>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
    $[46] = t24;
    $[47] = title;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t13 || $[50] !== t25) {
    t26 = <>{t13}{t25}</>;
    $[49] = t13;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  return t26;
}

/* ===================================================================== */
/* Popover                                                                */
/* ===================================================================== */
export function Popover({
  trigger,
  children,
  align = "start",
  side = "bottom",
  avoidViewportBottom = 8,
  label,
  className
}: {
  trigger: (p: {
    open: boolean;
    toggle: () => void;
    id: string;
  }) => React.ReactNode;
  children: React.ReactNode;
  align?: "start" | "end";
  side?: "top" | "bottom" | "auto";
  /** Fixed chrome (such as the phone tab bar) the panel must not cover. */
  avoidViewportBottom?: number;
  label: string;
  /** Extra class on the panel — e.g. a menu that needs its own max height. */
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<React.CSSProperties>({});
  const wrap = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(false), []);

  /**
   * Where the panel goes, in viewport coordinates.
   *
   * It is measured from the trigger rather than inherited from a positioned
   * ancestor, because the panel no longer HAS the trigger as an ancestor — see
   * the portal note below.
   *
   * `align` is logical and the inset it produces is physical: "end" is the
   * right edge in LTR and the left edge in RTL, so the flag is resolved
   * against the document's direction once, here, instead of by a mirrored
   * stylesheet.
   */
  const place = useCallback(() => {
    const el = wrap.current?.firstElementChild as HTMLElement | null;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rtl = document.documentElement.dir === "rtl";
    const pinRight = align === "end" ? !rtl : rtl;
    const vw = window.innerWidth;

    /**
     * CLAMPED TO THE VIEWPORT, NOT JUST ANCHORED TO THE TRIGGER.
     *
     * Anchoring alone put the panel off the screen edge: a 288px menu hung
     * from a trigger 195px from the left of a 392px phone needs 483px of
     * room. The trigger's edge is where the panel WANTS to be; the clamp is
     * where it is allowed to be.
     *
     * The panel's own width is only knowable once it exists, so this reads 0
     * on the opening frame and the effect below re-runs it after mount.
     * Math.max(8, …) on the ceiling keeps a panel wider than the viewport
     * pinned to the near edge instead of being pushed off the far one.
     */
    const pw = panel.current?.getBoundingClientRect().width ?? 0;
    const fit = (want: number) => {
      const ceiling = pw > 0 ? Math.max(8, vw - pw - 8) : Infinity;
      return Math.round(Math.min(Math.max(8, want), ceiling));
    };
    const s: React.CSSProperties = pinRight ? {
      right: fit(vw - r.right)
    } : {
      left: fit(r.left)
    };
    /**
     * NEVER PAST EITHER EDGE OF THE VIEWPORT.
     *
     * The panel flips up only when it does not fit below AND there is more
     * room above. Before this it flipped whenever the bottom was short, even
     * with less room above, and a tall menu opened off the top of the screen.
     * Whichever side it takes, `--v2-pop-room` caps its height to the room
     * there (ui.css), and the rest scrolls inside it.
     *
     * The height it needs is the content's (`scrollHeight`), not the box's: a
     * capped box would otherwise measure short and never flip.
     */
    const safeBottom = Math.max(8, avoidViewportBottom);
    const viewportBottom = window.innerHeight - safeBottom;
    const need = panel.current ? Math.max(panel.current.scrollHeight + 2, panel.current.getBoundingClientRect().height) : 0;
    const below = Math.round(viewportBottom - r.bottom - 8);
    const above = Math.round(r.top - 16);
    const opensTop = side === "top" ||
    // Before the panel has measured, reserve enough room for a useful menu.
    side === "auto" && (need || 180) > below && above > below;
    let room: number;
    if (opensTop) {
      s.bottom = Math.max(safeBottom, Math.round(window.innerHeight - r.top) + 8);
      room = window.innerHeight - (s.bottom as number) - 8;
    } else {
      // Below the trigger, never slid up over it: what does not fit scrolls.
      const top = Math.round(r.bottom) + 8;
      s.top = top;
      room = viewportBottom - top;
    }
    (s as Record<string, string | number>)["--v2-pop-room"] = `${Math.max(120, room)}px`;
    setBox(s);
  }, [align, avoidViewportBottom, side]);

  // Where the panel is portalled: the dialog the trigger sits in, else <body> (see the note at the portal).
  const [host, setHost] = useState<Element | null>(null);
  const toggle = useCallback(() => {
    setHost(wrap.current?.closest("dialog") ?? null);
    // Measured BEFORE the panel exists, so it never paints at 0,0 first.
    setOpen(v => {
      if (!v) place();
      return !v;
    });
  }, [place]);

  // The panel's width is unknown until it has mounted, so the first
  // placement is re-run once with the real measurement. Two paints, no flash:
  // the opening animation covers it.
  useEffect(() => {
    if (open) place();
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      // The panel is portalled out of `wrap`, so "outside" now means outside
      // BOTH — checking only the wrapper would close the menu on its own items.
      if (wrap.current?.contains(target) || panel.current?.contains(target)) return;
      close();
    };
    const onKey = (e_0: KeyboardEvent) => {
      if (e_0.key === "Escape") close();
    };
    let frame = 0;
    const reflow = () => {
      if (!frame) frame = window.requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };
    // `true` — capture, so a scroll inside any scrolling ancestor is seen, not
    // just a scroll of the document.
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", reflow, true);
      window.removeEventListener("resize", reflow);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [open, close, place]);
  return <div className="v2-pop-wrap" ref={wrap}>
      {trigger({
      open,
      toggle,
      id
    })}
      {/*
        THE PANEL IS PORTALLED TO <body>, AND THAT IS NOT A Z-INDEX WORKAROUND.
         `backdrop-filter` on an element makes it a BACKDROP ROOT: a descendant's
        own `backdrop-filter` samples only what is painted inside that root, not
        the page behind it. The top bar has glass, so every menu rendered inside
        it was blurring an empty backdrop — identical CSS to the rail, and
        visibly not the same material, because there was nothing there to
        refract. Rendered as a sibling of the shell it samples the real page and
        the glass is the rail's glass.
         The wrapper carries `.v2` because `<body>` is outside the shell and the
        tokens would otherwise be undefined; it is `display: contents`, so it
        adds a scope and no box.
       */}
      {/*
        INSIDE A DIALOG, INTO THE DIALOG. A modal <dialog> sits in the top
        layer and makes the rest of the page inert, so a panel portalled to
        <body> opened UNDER it (the bid dialog's wallet menu) and could not be
        clicked even if it showed. Portalled into the dialog it is in the top
        layer too; the dialog is `overflow: visible` with no transform once
        open, so `position: fixed` still means the viewport.
       */}
      {open && typeof document !== "undefined" ? createPortal(<div className="v2 v2-pop-portal">
              <div className={className ? `v2-pop ${className}` : "v2-pop"} id={id} ref={panel} role="menu" aria-label={label} style={box} onClick={close}>
                {children}
              </div>
            </div>, host ?? document.body) : null}
    </div>;
}

/* ===================================================================== */
/* Switch and segmented control                                           */
/* ===================================================================== */
export function Switch(t0) {
  const $ = _c(7);
  const {
    checked,
    onChange,
    label
  } = t0;
  let t1;
  if ($[0] !== checked || $[1] !== onChange) {
    t1 = () => onChange(!checked);
    $[0] = checked;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== checked || $[4] !== label || $[5] !== t1) {
    t2 = <button type="button" className="v2-switch v2-focus" role="switch" aria-checked={checked} aria-label={label} onClick={t1} />;
    $[3] = checked;
    $[4] = label;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function Segmented(t0) {
  const $ = _c(10);
  const {
    value,
    options,
    onChange,
    label
  } = t0;
  let t1;
  if ($[0] !== onChange || $[1] !== options || $[2] !== value) {
    let t2;
    if ($[4] !== onChange || $[5] !== value) {
      t2 = o => <button key={o.value} type="button" role="tab" aria-selected={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>;
      $[4] = onChange;
      $[5] = value;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = options.map(t2);
    $[0] = onChange;
    $[1] = options;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== label || $[8] !== t1) {
    t2 = <div className="v2-seg" role="tablist" aria-label={label}>{t1}</div>;
    $[7] = label;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/* ===================================================================== */
/* State — empty, filtered-empty or failed, in place of a grid.           */
/* ===================================================================== */
/** Copy and the one recovery action are the caller's. A failure passes
 *  `alert` so it is announced; an empty result is static and is not. */
export function StatePanel(t0) {
  const $ = _c(10);
  const {
    title,
    body,
    action,
    alert,
    className
  } = t0;
  const t1 = `v2-state ${className ?? ""}`;
  const t2 = alert ? "alert" : undefined;
  let t3;
  if ($[0] !== title) {
    t3 = <h3>{title}</h3>;
    $[0] = title;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== body) {
    t4 = body ? <p>{body}</p> : null;
    $[2] = body;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== action || $[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className={t1} role={t2}>{t3}{t4}{action}</div>;
    $[4] = action;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}

/* ===================================================================== */
/* Pagination — real hrefs, so crawlers and modifier-clicks still work.   */
/* ===================================================================== */
function pageWindow(current: number, last: number) {
  if (last <= 7) return Array.from({
    length: last
  }, (_, index) => index + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(last - 1, current + 1);
  if (start > 2) pages.push(-1);
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < last - 1) pages.push(-1);
  pages.push(last);
  return pages;
}
export function Pagination(t0) {
  const $ = _c(59);
  const {
    current,
    last,
    locale,
    label,
    buildHref,
    onPage
  } = t0;
  if (last <= 1) {
    return null;
  }
  const isFa = locale === "fa";
  let Next;
  let navigate;
  let next;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== buildHref || $[1] !== current || $[2] !== isFa || $[3] !== label || $[4] !== last || $[5] !== onPage) {
    const number = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
    let t5;
    if ($[13] !== onPage) {
      t5 = page => event => {
        if (!onPage || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        onPage(page);
      };
      $[13] = onPage;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    navigate = t5;
    const Previous = isFa ? ChevronRight : ChevronLeft;
    Next = isFa ? ChevronLeft : ChevronRight;
    const prev = Math.max(1, current - 1);
    next = Math.min(last, current + 1);
    t2 = "v2-pagination";
    t3 = label;
    let t6;
    if ($[15] !== buildHref || $[16] !== prev) {
      t6 = buildHref(prev);
      $[15] = buildHref;
      $[16] = prev;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== navigate || $[19] !== prev) {
      t7 = navigate(prev);
      $[18] = navigate;
      $[19] = prev;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const t8 = isFa ? "\u0635\u0641\u062D\u0647\u0654 \u0642\u0628\u0644\u06CC" : "Previous page";
    const t9 = current === 1 || undefined;
    const t10 = current === 1 ? -1 : undefined;
    let t11;
    if ($[21] !== Previous) {
      t11 = <Previous aria-hidden={true} />;
      $[21] = Previous;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const t12 = isFa ? "\u0642\u0628\u0644\u06CC" : "Previous";
    let t13;
    if ($[23] !== t12) {
      t13 = <span className="v2-pagination-label">{t12}</span>;
      $[23] = t12;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    if ($[25] !== t10 || $[26] !== t11 || $[27] !== t13 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
      t4 = <Link href={t6} onClick={t7} aria-label={t8} aria-disabled={t9} tabIndex={t10} className="v2-focus">{t11}{t13}</Link>;
      $[25] = t10;
      $[26] = t11;
      $[27] = t13;
      $[28] = t6;
      $[29] = t7;
      $[30] = t8;
      $[31] = t9;
      $[32] = t4;
    } else {
      t4 = $[32];
    }
    t1 = pageWindow(current, last).map((page_0, index) => page_0 === -1 ? <li key={`gap-${index}`} aria-hidden={true}>…</li> : <li key={page_0}><Link href={buildHref(page_0)} onClick={navigate(page_0)} aria-current={page_0 === current ? "page" : undefined} className="v2-focus">{number.format(page_0)}</Link></li>);
    $[0] = buildHref;
    $[1] = current;
    $[2] = isFa;
    $[3] = label;
    $[4] = last;
    $[5] = onPage;
    $[6] = Next;
    $[7] = navigate;
    $[8] = next;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    Next = $[6];
    navigate = $[7];
    next = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
  }
  let t5;
  if ($[33] !== t1) {
    t5 = <ol>{t1}</ol>;
    $[33] = t1;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  let t6;
  if ($[35] !== buildHref || $[36] !== next) {
    t6 = buildHref(next);
    $[35] = buildHref;
    $[36] = next;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== navigate || $[39] !== next) {
    t7 = navigate(next);
    $[38] = navigate;
    $[39] = next;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  const t8 = isFa ? "\u0635\u0641\u062D\u0647\u0654 \u0628\u0639\u062F\u06CC" : "Next page";
  const t9 = current === last || undefined;
  const t10 = current === last ? -1 : undefined;
  const t11 = isFa ? "\u0628\u0639\u062F\u06CC" : "Next";
  let t12;
  if ($[41] !== t11) {
    t12 = <span className="v2-pagination-label">{t11}</span>;
    $[41] = t11;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== Next) {
    t13 = <Next aria-hidden={true} />;
    $[43] = Next;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t10 || $[46] !== t12 || $[47] !== t13 || $[48] !== t6 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t14 = <Link href={t6} onClick={t7} aria-label={t8} aria-disabled={t9} tabIndex={t10} className="v2-focus">{t12}{t13}</Link>;
    $[45] = t10;
    $[46] = t12;
    $[47] = t13;
    $[48] = t6;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t14 || $[54] !== t2 || $[55] !== t3 || $[56] !== t4 || $[57] !== t5) {
    t15 = <nav className={t2} aria-label={t3}>{t4}{t5}{t14}</nav>;
    $[53] = t14;
    $[54] = t2;
    $[55] = t3;
    $[56] = t4;
    $[57] = t5;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  return t15;
}

/* ===================================================================== */
/* Avatar — a square mount, never a circle: the page has no round shapes. */
/* ===================================================================== */
export function Avatar(t0) {
  const $ = _c(15);
  const {
    name,
    src,
    size: t1
  } = t0;
  const size = t1 === undefined ? 40 : t1;
  let t2;
  if ($[0] !== src) {
    t2 = profileImageUrl(src);
    $[0] = src;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const image = t2;
  const [failed, setFailed] = useState(null);
  const fill = size === "fill";
  let t3;
  if ($[2] !== fill || $[3] !== size) {
    t3 = fill ? undefined : {
      inlineSize: size,
      blockSize: size,
      fontSize: Math.round(size * 0.38)
    };
    $[2] = fill;
    $[3] = size;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const style = t3;
  const t4 = fill ? "v2-avatar v2-avatar--fill" : "v2-avatar";
  let t5;
  if ($[5] !== failed || $[6] !== fill || $[7] !== image || $[8] !== name || $[9] !== size) {
    t5 = image && failed !== image ? fill ? <Image src={image} alt="" fill={true} sizes="160px" onError={() => setFailed(image)} /> : <Image src={image} alt="" width={size} height={size} sizes={`${size}px`} onError={() => setFailed(image)} /> : name.trim().charAt(0).toLocaleUpperCase() || "\u2022";
    $[5] = failed;
    $[6] = fill;
    $[7] = image;
    $[8] = name;
    $[9] = size;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== style || $[12] !== t4 || $[13] !== t5) {
    t6 = <span className={t4} style={style} aria-hidden={true}>{t5}</span>;
    $[11] = style;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/* ===================================================================== */
/* Loading — the turning star plus an announced label.                   */
/* ===================================================================== */
export function Loading(t0) {
  const $ = _c(6);
  const {
    label,
    className
  } = t0;
  const t1 = `v2-loading ${className ?? ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Spinner />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== label) {
    t3 = <span className="v2-sr-only">{label}</span>;
    $[1] = label;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <div className={t1} role="status">{t2}{t3}</div>;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}